import React, { PureComponent } from 'react';
import { layer, source, feature, interaction } from 'react-openlayers';
import GeometryType from 'ol/geom/GeometryType';
import { BLACK, WHITE } from '../../../rgbColors';
import { getCurrentMission } from '../../../dataModelHelpers';

import EventBus from '../../../EventBus';

import { MISSION_EVENTS } from '../../../missionEvents';
import { MAP_EVENTS } from '../../../mapEvents';
import { convertProjection4329 } from '../../../utils';
import { Feature, GeometryObject, LineString, Point } from 'geojson';
import { FIELD_ORDER_FEATURE, segmentArrow } from '../helpers/fieldOrder';
import { SCHEDULE_OVERLAY_PROPERTY } from '../helpers/features';

interface MapMakerScheduleLayerProps {
  scheduleFeatures: Feature<GeometryObject>[];
  showScheduledJobs: boolean;
  showOrderLine: boolean;
  showFieldOrder: boolean;
  missionActive: string;
  loadMissionMaps: (jobId: string) => Promise<void>;
}

// The field order overlay rides above the boundaries it annotates.
const Z_INDEX_FIELD_ORDER = 2;

export default class MapMakerScheduleLayer extends PureComponent<MapMakerScheduleLayerProps> {
  FIELD_TRANSPARENCY = '40'; // 25% opacity
  FIELD_ORDER_COLOR = '#ffffff';
  ORDER_BADGE_RADIUS = 20;
  fieldKeys: string[];
  prevMissionLat: null | number;
  prevMissionLon: null | number;

  constructor(props) {
    super(props);

    this.fieldKeys = [];

    this.updateMissionPullin = this.updateMissionPullin.bind(this);

    this.prevMissionLat = null;
    this.prevMissionLon = null;
  }

  componentDidMount() {
    // Only pullin changes matter — STATIC_POINTS_UPDATED fires from
    // update_pullin / undo. CREATED_OR_DELETED catches mission swap
    // (new mission may have a different pullin position).
    EventBus.on(MAP_EVENTS.STATIC_POINTS_UPDATED, this.updateMissionPullin);
    EventBus.on(MISSION_EVENTS.CREATED_OR_DELETED, this.updateMissionPullin);
  }

  componentWillUnmount() {
    EventBus.remove(MAP_EVENTS.STATIC_POINTS_UPDATED, this.updateMissionPullin);
    EventBus.remove(MISSION_EVENTS.CREATED_OR_DELETED, this.updateMissionPullin);
  }

  updateMissionPullin() {
    const mission = getCurrentMission();

    if (mission && (mission.pullin_lat !== this.prevMissionLat || mission.pullin_lon !== this.prevMissionLon)) {
      this.forceUpdate();
      this.prevMissionLat = mission.pullin_lat;
      this.prevMissionLon = mission.pullin_lon;
    }
  }

  /**
   * Handle that calls and returns output from gen function based on type.
   * @param {object} scheduleFeature GeoJSON object
   * @returns {PolygonReact | MultiPolygonReact | PointReact} The react component generated
   */
  genScheduleFeature(scheduleFeature: Feature<GeometryObject>, idx: number) {
    const jobID = scheduleFeature.properties?.['Rogo Job ID'];
    const mission = getCurrentMission();

    switch (scheduleFeature.geometry.type) {
      case GeometryType.MULTI_POLYGON:

      case GeometryType.POLYGON:
        if (this.props.showScheduledJobs) {
          return (!mission || mission.job_id !== jobID) && this.genFieldBoundary(scheduleFeature, idx);
        } else {
          return null;
        }

      case GeometryType.LINE_STRING:
        if (scheduleFeature.properties?.name === FIELD_ORDER_FEATURE) {
          return this.props.showFieldOrder ? this.genFieldOrderHop(scheduleFeature, idx) : null;
        }

        if (this.props.showOrderLine) {
          return this.genOrderLine(scheduleFeature);
        } else {
          return null;
        }

      case GeometryType.POINT:
        if (this.props.showScheduledJobs) {
          if (scheduleFeature.properties?.name === 'pullin') {
            return (!mission || mission.job_id !== jobID) && this.genPullinPoint(scheduleFeature);
          } else {
            return null;
          }
        } else {
          return null;
        }

      default:
        console.log('Unknown geometry type', scheduleFeature);

        return null;
    }
  }

  geometryIs<T extends GeometryObject>(feature: Feature<any>, type: GeometryType): feature is Feature<T> {
    return feature.geometry.type === type;
  }

  /**
   * Properties for one of this layer's features. Everything this layer
   * draws describes OTHER jobs in the shift — reference information,
   * not the mission being mapped — so every feature carries the
   * schedule-overlay marker and stays out of the map's Select / Modify
   * interactions (see `isScheduleOverlay`).
   *
   * The caller's GeoJSON properties are copied, never mutated: the same
   * objects are shared with ScheduleFieldZoomButton and the other
   * schedule layers.
   */
  overlayProperties(properties?: { [name: string]: any } | null) {
    return { ...properties, [SCHEDULE_OVERLAY_PROPERTY]: true };
  }

  /**
   * Generate operator schedule field boundary
   * @param {object} scheduleFeature GeoJSON object
   * @returns {MultiPolygonReact | PolygonReact} The react component generated
   */
  genFieldBoundary(scheduleFeature: Feature<GeometryObject>, idx: number) {
    // TODO This was not working correctly, will revisit later
    //if (!this.geometryIs<MultiPolygon>(scheduleFeature, GeometryType.MULTI_POLYGON) || !this.geometryIs<Polygon>(scheduleFeature, GeometryType.POLYGON)) return;
    const FieldFeature =
      (scheduleFeature.geometry as any).type === GeometryType.MULTI_POLYGON
        ? feature.MultiPolygonReact
        : feature.PolygonReact;

    const fieldColor = scheduleFeature.properties?.['MapMade'] ? '#43a047' : '#cc0000';
    const fieldKey = `${scheduleFeature.properties?.['Field from Form']}-Field-${scheduleFeature.properties?.['Rogo Job ID']}`;
    const showName = !this.fieldKeys.includes(fieldKey);

    if (showName) {
      this.fieldKeys.push(fieldKey);
    }

    // A field wears its place in the shift once, on the part that carries the
    // name — the name lifts to make room for it.
    const order = scheduleFeature.properties?.['Shift Order'];
    const labelPoint = showName && order ? scheduleFeature.properties?.['Order Point'] : null;

    return (
      <React.Fragment key={`${fieldKey}-${idx}`}>
        {[
          <FieldFeature
            coordinates={(scheduleFeature.geometry as any).coordinates}
            strokeOptions={{ color: fieldColor, width: 4 }}
            fillOptions={{ color: `${fieldColor}${this.FIELD_TRANSPARENCY}` }}
            textOptions={
              showName
                ? {
                    text: scheduleFeature.properties?.['Field from Form'],
                    font: '14px Calibri,sans-serif',
                    overflow: true,
                    offsetY: labelPoint ? -this.ORDER_BADGE_RADIUS - 8 : 0,
                    strokeOptions: { color: BLACK, width: 2 },
                    fillOptions: { color: WHITE },
                  }
                : {}
            }
            hideTextZoom={12}
            key={`${fieldKey}-${idx}`}
            properties={this.overlayProperties(scheduleFeature.properties)}
          />,
          labelPoint && this.genOrderBadge(labelPoint, order, `${fieldKey}-order`),
        ]}
      </React.Fragment>
    );
  }

  /**
   * Generate the disc a field wears to show its place in the shift. Filled so
   * the number reads against imagery of any shade.
   */
  genOrderBadge(coordinate: number[], order: number, key: string) {
    return (
      <feature.PointReact
        key={key}
        coordinate={coordinate}
        circleOptions={{
          radius: this.ORDER_BADGE_RADIUS,
          fillOptions: { color: WHITE },
          strokeOptions: { color: BLACK, width: 2 },
        }}
        textOptions={{
          text: String(order),
          font: 'bold 26px Calibri,sans-serif',
          overflow: true,
          fillOptions: { color: BLACK },
        }}
        zIndex={Z_INDEX_FIELD_ORDER}
        properties={this.overlayProperties()}
      />
    );
  }
  /**
   * Generate the pullin point for the operator schedule field
   * @param {object} scheduleFeature GeoJSON object
   * @returns {PointReact} The react component generated
   */
  genPullinPoint(scheduleFeature: Feature<GeometryObject>) {
    if (!this.geometryIs<Point>(scheduleFeature, GeometryType.POINT)) {
      return;
    }

    const pullinColor = scheduleFeature.properties?.['MapMade'] ? '#43a047' : '#cc0000';
    const showName = scheduleFeature.properties?.['noBoundary'];

    return (
      <feature.PointReact
        coordinate={scheduleFeature.geometry.coordinates}
        textOptions={{
          text: showName ? scheduleFeature.properties?.['Field from Form'] : '',
          font: '14px Calibri,sans-serif',
          overflow: true,
          strokeOptions: { color: BLACK, width: 2 },
          fillOptions: { color: WHITE },
        }}
        circleOptions={{ radius: 6, fillOptions: { color: pullinColor }, strokeOptions: { width: 1, color: WHITE } }}
        key={`${scheduleFeature.properties?.['Field from Form']}-Pullin-${scheduleFeature.properties?.['Rogo Job ID']}`}
        properties={this.overlayProperties(scheduleFeature.properties)}
      />
    );
  }

  /**
   * Generate one hop of the shift's field order: the connector from one field's
   * number to the next one's, and an arrow showing which way it runs. The field
   * being map-made wears no number, so its hops meet its border instead. The
   * numbers themselves are worn by the fields.
   * @param {object} scheduleFeature GeoJSON LineString built by fieldOrderFeatures
   * @returns {JSX.Element} The react components generated
   */
  genFieldOrderHop(scheduleFeature: Feature<GeometryObject>, idx: number) {
    if (!this.geometryIs<LineString>(scheduleFeature, GeometryType.LINE_STRING)) {
      return;
    }

    const [from, to] = scheduleFeature.geometry.coordinates;
    const arrow = segmentArrow(from, to);
    const key = `field-order-${scheduleFeature.properties?.['fromOrder']}-${scheduleFeature.properties?.['toOrder']}-${idx}`;

    return (
      <React.Fragment key={key}>
        {[
          <feature.LineStringReact
            key={`${key}-line`}
            coordinates={scheduleFeature.geometry.coordinates}
            strokeOptions={{ color: this.FIELD_ORDER_COLOR, width: 3, lineDash: [10, 6] }}
            zIndex={Z_INDEX_FIELD_ORDER}
            properties={this.overlayProperties(scheduleFeature.properties)}
          />,
          arrow && (
            <feature.PointReact
              key={`${key}-arrow`}
              coordinate={arrow.coordinate}
              textOptions={{
                text: '➤',
                font: 'bold 20px Calibri,sans-serif',
                strokeOptions: { color: BLACK, width: 3 },
                fillOptions: { color: WHITE },
                rotation: arrow.rotation,
              }}
              zIndex={Z_INDEX_FIELD_ORDER}
              properties={this.overlayProperties(scheduleFeature.properties)}
            />
          ),
        ]}
      </React.Fragment>
    );
  }

  genOrderLine(scheduleFeature: Feature<GeometryObject>) {
    if (!this.geometryIs<LineString>(scheduleFeature, GeometryType.LINE_STRING)) {
      return;
    }

    const mission = getCurrentMission();

    if (mission) {
      scheduleFeature.geometry.coordinates[scheduleFeature.properties?.['loadedMissionIdx']] = convertProjection4329([
        mission.pullin_lon,
        mission.pullin_lat,
      ]);
    }

    return (
      <feature.LineStringReact
        key={scheduleFeature.geometry.coordinates.slice(0, 2).join('-')}
        coordinates={scheduleFeature.geometry.coordinates}
        strokeOptions={{ color: scheduleFeature.properties?.['complete'] ? '#43a047B2' : '#cc0000B2', width: 4 }}
        properties={this.overlayProperties(scheduleFeature.properties)}
      />
    );
  }

  /**
   * Select handle that allows user to select a field to load from the map
   * @param {*} event
   */
  handleSelectFeature(feature) {
    console.log('feat', feature);

    if (feature) {
      const properties = feature.getProperties();

      if (properties.name === 'field') {
        this.props.loadMissionMaps(properties['Rogo Job ID']);
      }
    }

    return false;
  }

  render() {
    this.fieldKeys = [];

    return (
      // @ts-ignore
      <layer.VectorImage zIndex={1}>
        {/*
                // @ts-ignore */}
        <source.VectorSourceReact>
          {this.props.scheduleFeatures.map((feature, idx) => this.genScheduleFeature(feature, idx))}
          {!Boolean(this.props.missionActive) && (
            <interaction.SelectReact filter={this.handleSelectFeature.bind(this)} style={null} />
          )}
        </source.VectorSourceReact>
      </layer.VectorImage>
    );
  }
}
